import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ShieldAlert,
  Radar,
  Sparkles,
  Ban,
  TriangleAlert,
  Check,
  ChevronDown,
  Loader2,
  MessageSquareWarning,
  Flag as FlagIcon,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState } from "@/components/shared";

function riskTone(r) {
  if (r >= 75) return "red";
  if (r >= 50) return "amber";
  return "green";
}
function riskLabel(r) {
  if (r >= 75) return "High";
  if (r >= 50) return "Medium";
  return "Low";
}

const ACTION_META = {
  ban: { tone: "red", label: "Ban", icon: Ban },
  warn: { tone: "amber", label: "Warn", icon: TriangleAlert },
  dismiss: { tone: "muted", label: "Dismiss", icon: Check },
};

function parseAi(ai) {
  if (!ai) return null;
  if (typeof ai === "object") return ai;
  try {
    return JSON.parse(ai);
  } catch {
    return null;
  }
}

function AiVerdict({ ai }) {
  const v = parseAi(ai);
  if (!v) return null;
  const sa = (v.suggested_action || "").toLowerCase();
  const meta = ACTION_META[sa];
  return (
    <div className="rounded-xl bg-[hsl(189_70%_14%)] ring-1 ring-primary/25 p-4 mt-3" data-testid="ai-verdict">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-primary font-semibold mb-2.5">
        <Sparkles className="h-3.5 w-3.5" /> AI assessment
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {v.risk_level && (
          <Tag tone={v.risk_level === "high" ? "red" : v.risk_level === "medium" ? "amber" : "green"}>
            Risk: {v.risk_level}
          </Tag>
        )}
        {v.category && <Tag tone="blue">{v.category}</Tag>}
      </div>
      {v.reason && <p className="text-sm text-foreground/90 leading-relaxed">{v.reason}</p>}
      {meta && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Suggested action:</span>
          <Tag tone={meta.tone} data-testid="ai-suggested-action">
            <meta.icon className="h-3 w-3" /> {meta.label}
          </Tag>
        </div>
      )}
    </div>
  );
}

function FlagCard({ flag, resolved, open, onToggle, onAskAi, onReview, analyzing, busyAction }) {
  const u = flag.user || {};
  const ai = flag.ai;
  const suggested = (parseAi(ai)?.suggested_action || "").toLowerCase();
  const displayName = u.name || flag.user_id || "Unknown";

  return (
    <div className="rounded-2xl bg-card ring-1 ring-border/70 overflow-hidden" data-testid={`flag-${flag.flag_id}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Risk */}
          <div className="shrink-0 text-center">
            <div
              className={`h-14 w-14 rounded-xl grid place-items-center font-display text-xl ring-1 ${
                riskTone(flag.risk) === "red"
                  ? "bg-red-500/15 text-red-300 ring-red-500/30"
                  : riskTone(flag.risk) === "amber"
                  ? "bg-amber-500/15 text-amber-300 ring-amber-500/30"
                  : "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
              }`}
              data-testid="risk-badge"
            >
              {flag.risk}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">{riskLabel(flag.risk)}</div>
          </div>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <Avatar src={u.photo} name={u.name} size={40} />
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-1.5">
                  {displayName}
                  {u.banned && <Tag tone="red"><Ban className="h-3 w-3" />Banned</Tag>}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.email || flag.user_id}
                </div>
              </div>
              <div className="ml-auto">
                <Tag tone={flag.type === "message" ? "amber" : "blue"}>
                  {flag.type === "message" ? <MessageSquareWarning className="h-3 w-3" /> : <FlagIcon className="h-3 w-3" />}
                  {flag.type}
                </Tag>
              </div>
            </div>

            <p className="text-sm text-foreground/90 mt-3">{flag.detail}</p>

            {flag.sample && (
              <blockquote className="mt-3 border-l-2 border-primary/50 pl-3 py-1 text-sm text-muted-foreground italic bg-secondary/30 rounded-r" data-testid="flag-sample">
                “{flag.sample}”
              </blockquote>
            )}

            {open && (
              <div data-testid="flag-expanded">
                {analyzing ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Analyzing with AI…
                  </div>
                ) : ai ? (
                  <AiVerdict ai={ai} />
                ) : (
                  <p className="text-sm text-muted-foreground mt-3">No AI assessment yet.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border/50">
          {!resolved && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAskAi(flag)}
                disabled={analyzing}
                data-testid={`ask-ai-${flag.flag_id}`}
              >
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Ask AI
              </Button>
              <div className="flex-1" />
              <Button
                variant={suggested === "dismiss" ? "default" : "outline"}
                size="sm"
                onClick={() => onReview(flag.flag_id, "dismiss")}
                disabled={busyAction}
                data-testid={`dismiss-${flag.flag_id}`}
              >
                <Check className="h-4 w-4" /> Dismiss
              </Button>
              <Button
                size="sm"
                className={suggested === "warn" ? "bg-amber-500 hover:bg-amber-400 text-black" : ""}
                variant={suggested === "warn" ? "default" : "outline"}
                onClick={() => onReview(flag.flag_id, "warn")}
                disabled={busyAction}
                data-testid={`warn-${flag.flag_id}`}
              >
                <TriangleAlert className="h-4 w-4" /> Warn
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onReview(flag.flag_id, "ban")}
                disabled={busyAction}
                data-testid={`ban-${flag.flag_id}`}
              >
                <Ban className="h-4 w-4" /> Ban
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onToggle(flag)} data-testid={`expand-${flag.flag_id}`}>
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </Button>
            </>
          )}
          {resolved && (
            <>
              <Tag tone="green"><Check className="h-3 w-3" />Resolved</Tag>
              {ai && (
                <Button variant="ghost" size="sm" onClick={() => onToggle(flag)} className="ml-auto" data-testid={`toggle-${flag.flag_id}`}>
                  {open ? "Hide" : "View"} AI <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TrustSafety() {
  const [tab, setTab] = useState("open");
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [busyActionId, setBusyActionId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const reqRef = useRef(0);

  const resolved = tab === "history";

  const load = useCallback(
    async ({ silent } = {}) => {
      const myReq = ++reqRef.current;
      const wantResolved = resolved;
      if (!silent) setLoading(true);
      setError("");
      try {
        const res = await api.safetyQueue(wantResolved);
        if (myReq !== reqRef.current) return; // stale response, ignore
        const list = Array.isArray(res) ? res : res?.flags || res?.queue || [];
        setFlags(list);
      } catch (e) {
        if (myReq !== reqRef.current) return;
        setError(e.message || "Failed to load safety queue");
        setFlags([]);
      } finally {
        if (!silent && myReq === reqRef.current) setLoading(false);
      }
    },
    [resolved]
  );

  useEffect(() => {
    setExpandedId(null);
    load();
  }, [load]);

  const analyze = async (flagId) => {
    setAnalyzingId(flagId);
    try {
      await api.safetyAnalyze(flagId);
      await load({ silent: true }); // silent refetch keeps cards mounted & expanded
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAnalyzingId(null);
    }
  };

  const toggleExpand = (flag) => {
    setExpandedId((prev) => {
      const next = prev === flag.flag_id ? null : flag.flag_id;
      if (next && !flag.ai) analyze(flag.flag_id);
      return next;
    });
  };

  const askAi = (flag) => {
    setExpandedId(flag.flag_id);
    analyze(flag.flag_id);
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.safetyScan();
      toast.success(`Scan complete · ${res?.flags_upserted ?? 0} flag(s) updated`);
      await load({ silent: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setScanning(false);
    }
  };

  const review = async (flagId, action) => {
    setBusyActionId(flagId);
    try {
      await api.safetyReview(flagId, action);
      const msg = action === "ban" ? "User banned" : action === "warn" ? "Warning issued" : "Flag dismissed";
      toast.success(`${msg} · flag resolved`);
      if (expandedId === flagId) setExpandedId(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyActionId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Trust & Safety"
        subtitle="Rule-based risk sweep with AI-assisted review of flagged members."
        actions={
          <Button onClick={runScan} disabled={scanning} data-testid="run-scan-btn">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
            Run scan
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList data-testid="safety-tabs">
          <TabsTrigger value="open" data-testid="tab-open">Open</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
      </Tabs>

      {error && (
        <div className="rounded-xl bg-destructive/15 ring-1 ring-destructive/40 px-4 py-3 text-sm mb-4 flex items-center gap-2" data-testid="safety-error">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : flags.length === 0 && !error ? (
        <EmptyState
          icon={ShieldAlert}
          title={resolved ? "No resolved flags yet" : "No open flags"}
          hint={resolved ? "Reviewed flags will appear here." : "Run a scan to sweep for risky members."}
        />
      ) : (
        <div className="space-y-3" data-testid="safety-queue">
          {flags.map((f) => (
            <FlagCard
              key={f.flag_id}
              flag={f}
              resolved={resolved}
              open={expandedId === f.flag_id}
              analyzing={analyzingId === f.flag_id}
              busyAction={busyActionId === f.flag_id}
              onToggle={toggleExpand}
              onAskAi={askAi}
              onReview={review}
            />
          ))}
        </div>
      )}
    </div>
  );
}
